/**
 * Tooltip Component
 * Lightweight tooltip for showing help text on hover
 */
import React, { useState, useRef, useEffect } from 'react';
import { Info } from 'lucide-react';

export const Tooltip = ({ children, content, position = 'top', delay = 200 }) => {
  const [show, setShow] = useState(false);
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const triggerRef = useRef(null);
  const timeoutRef = useRef(null);

  const handleMouseEnter = () => {
    timeoutRef.current = setTimeout(() => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setCoords({
          x: rect.left + rect.width / 2,
          y: position === 'top' ? rect.top : rect.bottom,
        });
      }
      setShow(true);
    }, delay);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setShow(false);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const tooltipStyle = {
    position: 'fixed',
    left: coords.x,
    top: position === 'top' ? coords.y - 8 : coords.y + 8,
    transform: `translate(-50%, ${position === 'top' ? '-100%' : '0'})`,
    padding: '8px 12px',
    background: 'rgba(0, 0, 0, 0.9)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '8px',
    color: 'rgba(255, 255, 255, 0.9)',
    fontSize: '11px',
    lineHeight: '1.4',
    maxWidth: '250px',
    zIndex: 10000,
    pointerEvents: 'none',
    animation: 'tooltipFadeIn 0.15s ease',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
  };

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ display: 'inline-flex', cursor: 'help' }}
      >
        {children}
      </span>
      {show && content && <div style={tooltipStyle}>{content}</div>}
      <style>{`
        @keyframes tooltipFadeIn {
          from { opacity: 0; transform: translate(-50%, ${position === 'top' ? 'calc(-100% + 4px)' : '-4px'}); }
          to { opacity: 1; transform: translate(-50%, ${position === 'top' ? '-100%' : '0'}); }
        }
      `}</style>
    </>
  );
};

/**
 * InfoTip - Info icon with tooltip
 * Use for inline help text next to form labels
 */
export const InfoTip = ({
  content,
  size = 12,
  color = 'rgba(255, 255, 255, 0.4)',
  position = 'top',
}) => (
  <Tooltip content={content} position={position}>
    <Info
      size={size}
      style={{
        color,
        marginLeft: '4px',
        verticalAlign: 'middle',
        flexShrink: 0,
      }}
    />
  </Tooltip>
);

/**
 * HelpText - Small help text below a field
 */
export const HelpText = ({ children, color = 'rgba(255, 255, 255, 0.4)' }) => (
  <div
    style={{
      fontSize: '9px',
      color,
      marginTop: '4px',
      lineHeight: '1.3',
    }}
  >
    {children}
  </div>
);

export default Tooltip;
